import { z } from 'zod';

// Quotation Status
export type QuotationStatus = 'draft' | 'sent' | 'received' | 'approved' | 'rejected' | 'expired';

// RFQ Types
export interface RFQ {
  id: string;
  rfq_number: string;
  pr_id: string;
  date: string;
  required_date: string;
  status: 'draft' | 'sent' | 'closed';
  terms_conditions?: string;
  notes?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  // Joined relations from fetchRFQs
  pr?: {
    id: string;
    code: string;
  };
  suppliers?: Array<{
    id: string;
    supplier: {
      id: string;
      name: string;
    };
    status: 'pending' | 'sent' | 'responded' | 'no_response';
    sent_at?: string;
    response_date?: string;
  }>;
  items?: RFQItem[];
}

export interface RFQSupplier {
  id: string;
  rfq_id: string;
  supplier_id: string;
  status: 'pending' | 'sent' | 'responded' | 'no_response';
  sent_at?: string;
  response_date?: string;
  created_at: string;
  updated_at: string;
}

export interface RFQItem {
  id: string;
  rfq_id: string;
  pr_item_id?: string | null;
  item_code: string;
  item_name: string;
  description?: string;
  quantity: number;
  unit: string;
  specifications?: string;
  created_at: string;
  updated_at: string;
}

// Quotation Types
export interface Quotation {
  id: string;
  quotation_number: string;
  rfq_id: string;
  supplier_id: string;
  date: string;
  valid_until: string;
  currency: string;
  exchange_rate?: number;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  payment_terms?: string;
  delivery_terms?: string;
  delivery_time?: number;
  warranty_terms?: string;
  status: QuotationStatus;
  notes?: string;
  created_at: string;
  updated_at: string;
  // Joined relations from fetchQuotations
  rfq?: {
    id: string;
    rfq_number: string;
    pr?: {
      id: string;
      code: string;
    };
  };
  supplier?: {
    id: string;
    name: string;
  };
  items?: QuotationItem[];
}

export interface QuotationItem {
  id: string;
  quotation_id: string;
  rfq_item_id: string;
  item_code: string;
  item_name: string;
  description?: string;
  quantity: number;
  unit: string;
  unit_price: number;
  tax_rate?: number;
  tax_amount?: number;
  total_amount: number;
  delivery_date?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

// Form Schemas
export const rfqSchema = z.object({
  pr_id: z.string().uuid(),
  required_date: z.string(),
  terms_conditions: z.string().optional(),
  notes: z.string().optional(),
  supplier_ids: z.array(z.string().uuid()).min(1, 'Select at least one supplier'),
  items: z.array(z.object({
    pr_item_id: z.string().uuid().optional().nullable(),
    item_code: z.string().min(1, 'Item code is required'),
    item_name: z.string().min(1, 'Item name is required'),
    description: z.string().optional(),
    quantity: z.number().positive('Quantity must be positive'),
    unit: z.string().min(1, 'Unit is required'),
    specifications: z.string().optional()
  })).min(1, 'Add at least one item')
});

export const quotationSchema = z.object({
  rfq_id: z.string().uuid(),
  supplier_id: z.string().uuid(),
  valid_until: z.string(),
  currency: z.string(),
  exchange_rate: z.number().optional(),
  payment_terms: z.string().optional(),
  delivery_terms: z.string().optional(),
  delivery_time: z.number().optional(),
  warranty_terms: z.string().optional(),
  notes: z.string().optional(),
  items: z.array(z.object({
    rfq_item_id: z.string().uuid(),
    item_code: z.string(),
    item_name: z.string(),
    description: z.string().optional(),
    quantity: z.number().positive(),
    unit: z.string(),
    unit_price: z.number().nonnegative(),
    tax_rate: z.number().optional(),
    delivery_date: z.string().optional(),
    notes: z.string().optional()
  })).min(1, 'Add at least one item')
});

export type RFQFormData = z.infer<typeof rfqSchema>;
export type QuotationFormData = z.infer<typeof quotationSchema>;